import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { bidService } from '../services/bidService';
import {
  FileText,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
} from 'lucide-react';

interface ArchDocExtractionProps {
  architecturalDocumentId: string;
}

export const ArchDocExtraction: React.FC<ArchDocExtractionProps> = ({ architecturalDocumentId }) => {
  const [archDoc, setArchDoc] = useState<any>(null);
  const [showRooms, setShowRooms] = useState(false);

  useEffect(() => {
    bidService.getArchitecturalDocument(architecturalDocumentId)
      .then(doc => setArchDoc(doc))
      .catch(err => console.error('Failed to fetch arch doc:', err));
  }, [architecturalDocumentId]);

  if (!archDoc?.extractedData) return null;

  const d = archDoc.extractedData;

  // Build headline chips
  const headlines: string[] = [];
  if (d.project?.projectType && d.project.projectType !== 'UNKNOWN') {
    const typeLabel: Record<string, string> = {
      'GROUND_UP': 'New Construction',
      'ADDITION': 'Addition',
      'RENOVATION': 'Renovation',
    };
    headlines.push(typeLabel[d.project.projectType] || d.project.projectType);
  }
  if (d.project?.stories) headlines.push(`${d.project.stories} Stories`);
  if (d.sizes?.livingSqft) headlines.push(`${d.sizes.livingSqft.toLocaleString()} sqft`);
  if (d.structureFlags?.foundationType && d.structureFlags.foundationType !== 'UNKNOWN') {
    const foundLabel: Record<string, string> = {
      'BASEMENT': 'Basement',
      'CRAWL': 'Crawl Space',
      'SLAB': 'Slab',
      'MIXED': 'Mixed Foundation',
    };
    headlines.push(foundLabel[d.structureFlags.foundationType] || d.structureFlags.foundationType);
  }
  const bedBath: string[] = [];
  if (d.spaceSummary?.bedroomCount) bedBath.push(`${d.spaceSummary.bedroomCount} Bed`);
  if (d.spaceSummary?.bathroomCount) bedBath.push(`${d.spaceSummary.bathroomCount} Bath`);
  if (bedBath.length > 0) headlines.push(bedBath.join(' / '));

  // Group spaces by level
  const spacesByLevel: Record<string, any[]> = {};
  if (d.spaces && d.spaces.length > 0) {
    for (const space of d.spaces) {
      const level = space.level || 'UNKNOWN';
      if (!spacesByLevel[level]) spacesByLevel[level] = [];
      spacesByLevel[level].push(space);
    }
  }
  const levelOrder = ['BASEMENT', 'LEVEL_1', 'LEVEL_2', 'LEVEL_3', 'ATTIC', 'UNKNOWN'];
  const levelLabels: Record<string, string> = {
    'BASEMENT': 'Basement',
    'LEVEL_1': 'First Floor',
    'LEVEL_2': 'Second Floor',
    'LEVEL_3': 'Third Floor',
    'ATTIC': 'Attic',
    'UNKNOWN': 'Other',
  };
  const sortedLevels = Object.keys(spacesByLevel).sort(
    (a, b) => levelOrder.indexOf(a) - levelOrder.indexOf(b)
  );

  // Collect key quantities for bid validation
  const quantities: { label: string; value: string | number }[] = [];
  if (d.geometry?.windowCount) quantities.push({ label: 'Windows', value: d.geometry.windowCount });
  if (d.geometry?.exteriorDoorCount) quantities.push({ label: 'Exterior Doors', value: d.geometry.exteriorDoorCount });
  if (d.geometry?.garageDoorCount) quantities.push({ label: 'Garage Doors', value: d.geometry.garageDoorCount });
  if (d.geometry?.fireplaceCount) quantities.push({ label: 'Fireplaces', value: d.geometry.fireplaceCount });
  if (d.mepFlags?.hvacSystemCount) quantities.push({ label: 'HVAC Systems', value: d.mepFlags.hvacSystemCount });
  if (d.mepFlags?.electricalServiceAmps) quantities.push({ label: 'Electrical Service', value: `${d.mepFlags.electricalServiceAmps}A` });
  if (d.mepFlags?.plumbingFixtureCount) quantities.push({ label: 'Plumbing Fixtures', value: d.mepFlags.plumbingFixtureCount });

  // Collect building dimensions
  const dimensions: { label: string; value: string }[] = [];
  if (d.sizes?.livingSqft) dimensions.push({ label: 'Living Area', value: `${d.sizes.livingSqft.toLocaleString()} sqft` });
  if (d.sizes?.aboveGradeSqft) dimensions.push({ label: 'Above Grade', value: `${d.sizes.aboveGradeSqft.toLocaleString()} sqft` });
  if (d.sizes?.belowGradeSqft) dimensions.push({ label: 'Below Grade', value: `${d.sizes.belowGradeSqft.toLocaleString()} sqft` });
  if (d.sizes?.basementSqft) dimensions.push({ label: 'Basement', value: `${d.sizes.basementSqft.toLocaleString()} sqft` });
  if (d.sizes?.footprintSqft) dimensions.push({ label: 'Footprint', value: `${d.sizes.footprintSqft.toLocaleString()} sqft` });
  if (d.sizes?.garageSqft) dimensions.push({ label: 'Garage', value: `${d.sizes.garageSqft.toLocaleString()} sqft` });
  if (d.sizes?.deckSqft) dimensions.push({ label: 'Deck', value: `${d.sizes.deckSqft.toLocaleString()} sqft` });
  if (d.sizes?.porchSqft) dimensions.push({ label: 'Porch', value: `${d.sizes.porchSqft.toLocaleString()} sqft` });
  if (d.sizes?.lotSqft) dimensions.push({ label: 'Lot', value: `${d.sizes.lotSqft.toLocaleString()} sqft` });
  if (d.geometry?.perimeterFt) dimensions.push({ label: 'Perimeter', value: `${d.geometry.perimeterFt} ft` });
  if (d.project?.buildingHeightFt) dimensions.push({ label: 'Building Height', value: `${d.project.buildingHeightFt} ft` });

  // Roof info
  const roofDetails: string[] = [];
  if (d.geometry?.roofType && d.geometry.roofType !== 'UNKNOWN') roofDetails.push(d.geometry.roofType);
  if (d.geometry?.roofPitch) roofDetails.push(d.geometry.roofPitch);
  if (d.geometry?.roofAreaSqft) roofDetails.push(`${d.geometry.roofAreaSqft.toLocaleString()} sqft`);

  // Structure details
  const structureDetails: { label: string; value: string }[] = [];
  if (d.structureFlags?.steelPresent) structureDetails.push({ label: 'Steel', value: 'Yes' });
  if (d.structureFlags?.engineeredLumberPresent) structureDetails.push({ label: 'Engineered Lumber', value: 'Yes' });
  if (d.structureFlags?.walkoutBasement) structureDetails.push({ label: 'Walkout Basement', value: 'Yes' });
  if (d.structureFlags?.retainingWalls) structureDetails.push({ label: 'Retaining Walls', value: 'Yes' });
  if (d.mepFlags?.sumpPump) structureDetails.push({ label: 'Sump Pump', value: 'Yes' });
  if (d.mepFlags?.backwaterValve) structureDetails.push({ label: 'Backwater Valve', value: 'Yes' });

  return (
    <Card className="border border-gray-200">
      <CardHeader className="bg-gray-50 pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <FileText className="h-5 w-5 text-blue-600" />
          <span className="font-semibold">Construction Documents</span>
          <Badge className="bg-blue-100 text-blue-800 border-blue-200 text-xs">
            {d.meta?.pageCount || '?'} pages
          </Badge>
        </CardTitle>
        {headlines.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {headlines.map((h, idx) => (
              <Badge key={idx} className="bg-gray-100 text-gray-800 border-gray-300 text-sm font-medium">
                {h}
              </Badge>
            ))}
          </div>
        )}
      </CardHeader>

      <CardContent className="p-6 space-y-5">
        {/* Building Dimensions & Roof */}
        {(dimensions.length > 0 || roofDetails.length > 0 || structureDetails.length > 0) && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {dimensions.length > 0 && (
              <div>
                <h4 className="font-semibold text-sm text-gray-700 mb-2">Building Dimensions</h4>
                <div className="text-sm space-y-1">
                  {dimensions.map((dim, idx) => (
                    <div key={idx} className="flex justify-between">
                      <span className="text-gray-500">{dim.label}</span>
                      <span className="font-medium">{dim.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {(roofDetails.length > 0 || structureDetails.length > 0) && (
              <div>
                <h4 className="font-semibold text-sm text-gray-700 mb-2">Structure</h4>
                <div className="text-sm space-y-1">
                  {roofDetails.length > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Roof</span>
                      <span className="font-medium">{roofDetails.join(' | ')}</span>
                    </div>
                  )}
                  {structureDetails.map((s, idx) => (
                    <div key={idx} className="flex justify-between">
                      <span className="text-gray-500">{s.label}</span>
                      <span className="font-medium">{s.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {quantities.length > 0 && (
              <div>
                <h4 className="font-semibold text-sm text-gray-700 mb-2">Key Quantities</h4>
                <div className="text-sm space-y-1">
                  {quantities.map((q, idx) => (
                    <div key={idx} className="flex justify-between">
                      <span className="text-gray-500">{q.label}</span>
                      <span className="font-medium">{q.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Room Schedule — grouped by level */}
        {sortedLevels.length > 0 && (
          <div>
            <div
              className="flex items-center justify-between cursor-pointer"
              onClick={() => setShowRooms(!showRooms)}
            >
              <h4 className="font-semibold text-sm text-gray-700">
                Room Schedule ({d.spaces.length} rooms)
              </h4>
              {showRooms ? (
                <ChevronUp className="h-4 w-4 text-gray-400" />
              ) : (
                <ChevronDown className="h-4 w-4 text-gray-400" />
              )}
            </div>
            {showRooms && (
              <div className="mt-2 border border-gray-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Room</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Type</th>
                      <th className="px-3 py-2 text-right font-medium text-gray-600">Area</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {sortedLevels.map(level => {
                      const rooms = spacesByLevel[level];
                      const levelTotal = rooms.reduce((sum: number, r: any) => sum + (r.areaSqft || 0), 0);
                      return (
                        <React.Fragment key={level}>
                          <tr className="bg-gray-50">
                            <td colSpan={2} className="px-3 py-1.5 font-semibold text-gray-700 text-xs uppercase tracking-wide">
                              {levelLabels[level] || level}
                            </td>
                            <td className="px-3 py-1.5 text-right font-semibold text-gray-700 text-xs">
                              {levelTotal > 0 ? `${levelTotal.toLocaleString()} sqft` : ''}
                            </td>
                          </tr>
                          {rooms.map((space: any, idx: number) => (
                            <tr key={`${level}-${idx}`}>
                              <td className="px-3 py-1.5 pl-6">{space.name}</td>
                              <td className="px-3 py-1.5 text-gray-500">{space.type}</td>
                              <td className="px-3 py-1.5 text-right">
                                {space.areaSqft ? `${space.areaSqft.toLocaleString()} sqft` : '—'}
                              </td>
                            </tr>
                          ))}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Missing Critical Data */}
        {d.missingCritical && d.missingCritical.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <h4 className="font-semibold text-sm text-amber-800 flex items-center gap-1.5 mb-1">
              <AlertTriangle className="h-4 w-4" />
              Data Not Found in Plans
            </h4>
            <ul className="text-sm text-amber-700 space-y-0.5 list-disc list-inside">
              {d.missingCritical.map((item: string, idx: number) => (
                <li key={idx}>{item}</li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
